import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import { NumberField } from 'src/Libs/inputs/react-hook-form'
import { UnitTypes } from 'src/Libs/types'
import { DeadBandThresholdSettingsForm, getDeadBandThresholdSettingsSchema } from '../types/schemas'
import { DeadBandThresholdSettingsModel, DynamicGridRegenerationSettingsModel } from '../types/grid-and-system-protection.model'
import SettingsFormContainer from 'src/Libs/form/components/SettingsFormContainer'
import useGridAndSystemProtectionMutation from '../hooks/use-grid-and-system-protection-mutation'
import useSyncFormWithValues from '../hooks/use-sync-form-with-values'
import { FUNCTION_NUMBER } from '../types/common'
import { getChangedFieldStyle, isFieldDisabled } from '../utils'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useFarmContext } from 'src/Elements/Farm'
const NAME_SPACE = 'systemSettings.grid-and-system-protection-configuration.dead-band-threshold'

type Props = {
  deadBandThresholdSettingsModel: DeadBandThresholdSettingsModel
  dynamicGridRegenerationSettingsModel: DynamicGridRegenerationSettingsModel
}

const DeadBandThresholdSettings: React.FC<Props> = ({ deadBandThresholdSettingsModel, dynamicGridRegenerationSettingsModel }) => {
  const { t } = useCustomTranslation()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const canEdit = isFarm ? hasInstallationWritingRights && isFarmMasterSelected : hasInstallationWritingRights
  const defaultValues = useMemo(() => getDefaultValues(deadBandThresholdSettingsModel), [deadBandThresholdSettingsModel])
  const methods = useForm<DeadBandThresholdSettingsForm>({
    resolver: zodResolver(getDeadBandThresholdSettingsSchema(deadBandThresholdSettingsModel)),
    defaultValues,
    mode: 'onBlur',
  })
  useSyncFormWithValues(methods.reset, defaultValues)

  const { isLoading, updateGridAndSystemProtection } = useGridAndSystemProtectionMutation()

  const onSubmit = useCallback(
    async (data: DeadBandThresholdSettingsForm): Promise<void> => {
      try {
        await updateGridAndSystemProtection(
          {
            deadBandThresholdSettings: data,
            // ? since BE doesn't accept partial DynamicGridPower payload we need to send dynamicGridRegenerationSettings as well
            dynamicGridRegenerationSettings: {
              dynamicReactiveCurrentControl: dynamicGridRegenerationSettingsModel.dynamicReactiveCurrentControl.isDisabled,
              kFactor: {
                positiveSequenceSystem: dynamicGridRegenerationSettingsModel.kFactor.positiveSequenceSystem.value,
                negativeSequenceSystem: dynamicGridRegenerationSettingsModel.kFactor.negativeSequenceSystem.value,
              },
            },
          },
          FUNCTION_NUMBER.dynamicGridPowerSettings
        )
        methods.reset(data)
      } catch (error) {
        methods.reset()
      }
    },
    [methods, updateGridAndSystemProtection, dynamicGridRegenerationSettingsModel]
  )

  return (
    <FormProvider {...methods}>
      <Grid item xs={12}>
        <AccordionItem title={t(`${NAME_SPACE}.title`)} accordionDetailsSx={{ padding: 0 }}>
          <Box component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <SettingsFormContainer onReset={methods.reset} isLoading={isLoading}>
              <SettingsBox
                title={t(`${NAME_SPACE}.reactive-current-control.title`)}
                suffix={
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <NumberField
                      name="reactiveCurrentControl.lower"
                      label={t(`${NAME_SPACE}.reactive-current-control.lower.label`)}
                      suffix={UnitTypes.volt}
                      disabled={!canEdit || isFieldDisabled(deadBandThresholdSettingsModel.reactiveCurrentControl.lower)}
                      sx={getChangedFieldStyle(deadBandThresholdSettingsModel.reactiveCurrentControl.lower.changed)}
                    />
                    <NumberField
                      name="reactiveCurrentControl.upper"
                      label={t(`${NAME_SPACE}.reactive-current-control.upper.label`)}
                      suffix={UnitTypes.volt}
                      disabled={!canEdit || isFieldDisabled(deadBandThresholdSettingsModel.reactiveCurrentControl.upper)}
                      sx={getChangedFieldStyle(deadBandThresholdSettingsModel.reactiveCurrentControl.upper.changed)}
                    />
                  </Box>
                }
              />
              <SettingsBox
                title={t(`${NAME_SPACE}.zero-current-mode.title`)}
                suffix={
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <NumberField
                      name="zeroCurrentMode.lower"
                      label={t(`${NAME_SPACE}.zero-current-mode.lower.label`)}
                      suffix={UnitTypes.volt}
                      disabled={!canEdit || isFieldDisabled(deadBandThresholdSettingsModel.zeroCurrentMode.lower)}
                      sx={getChangedFieldStyle(deadBandThresholdSettingsModel.zeroCurrentMode.lower.changed)}
                    />
                    <NumberField
                      name="zeroCurrentMode.upper"
                      label={t(`${NAME_SPACE}.zero-current-mode.upper.label`)}
                      suffix={UnitTypes.volt}
                      disabled={!canEdit || isFieldDisabled(deadBandThresholdSettingsModel.zeroCurrentMode.upper)}
                      sx={getChangedFieldStyle(deadBandThresholdSettingsModel.zeroCurrentMode.upper.changed)}
                    />
                  </Box>
                }
              />
            </SettingsFormContainer>
          </Box>
        </AccordionItem>
      </Grid>
    </FormProvider>
  )
}

export default DeadBandThresholdSettings

const getDefaultValues = (deadBandThresholdSettings: DeadBandThresholdSettingsModel): DeadBandThresholdSettingsForm => ({
  reactiveCurrentControl: {
    lower: deadBandThresholdSettings.reactiveCurrentControl.lower.value,
    upper: deadBandThresholdSettings.reactiveCurrentControl.upper.value,
  },
  zeroCurrentMode: {
    lower: deadBandThresholdSettings.zeroCurrentMode.lower.value,
    upper: deadBandThresholdSettings.zeroCurrentMode.upper.value,
  },
})
